import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { Box, Flex } from '@stoplight/mosaic'
import { Link, Redirect, useLocation } from 'react-router-dom'
import { ExportButtonProps, ParsedDocs } from '@stoplight/elements-core'

import { computeAPITree, findFirstNodeSlug } from '../utils/content'
import { TableOfContents } from './table-of-content/TableOfContents'

import { ServiceNode } from '../types'
import { Article } from './Article'

type ContentProps = {
  basePath?: string
  serviceNode: ServiceNode
  exportProps?: ExportButtonProps
}

export const Content = ({
  serviceNode,
  basePath = '',
  exportProps,
}: ContentProps) => {
  const location = useLocation()
  const { pathname } = location

  const { isLoading, data, isFetched, error } = useQuery(
    [pathname],
    async () => {
      const url = pathname.replace(basePath, '')
      const res = await fetch(`${process.env.PUBLIC_URL}${url}.md`)
      if (res.ok) {
        const data = await res.text()

        // If the article files is not found, the index file is loaded by default
        if (data.includes('You need to enable JavaScript to run this app.')) {
          throw new Error('Unable to load article')
        }

        return data
      }

      throw new Error(`Unable to load article, status code: ${res.status}`)
    },
    {
      enabled: pathname.includes('/articles/'),
    }
  )

  const tree = useMemo(
    () => computeAPITree(serviceNode, basePath),
    [serviceNode, basePath]
  )

  const isRootPath = !pathname || pathname === basePath
  const node = isRootPath
    ? serviceNode
    : serviceNode.children.find(
        (child) => `${basePath}${child.uri}` === pathname
      )

  if (!node && !pathname.includes('/articles/')) {
    // Redirect to the first child if node doesn't exist
    const firstSlug = findFirstNodeSlug(tree)

    if (firstSlug) {
      return <Redirect to={firstSlug} />
    }
  }

  return (
    <>
      <Box
        // @ts-expect-error
        w={70}
        id="sidebar-nav"
        bg="canvas"
        h="full"
        className="sl-max-h-main sl-border-r sl-absolute sl-overflow-y-scroll sl-overflow-x-auto"
      >
        <TableOfContents
          items={tree}
          Link={Link}
          activeId={pathname}
          maxDepthOpenByDefault={2}
        />
      </Box>
      <Box
        id="main-content"
        bg="canvas"
        className="sl-absolute sl-top-0 sl-bottom-0 sl-right-0 sl-left-70 sl-overflow-y-auto sl-overflow-x-hidden"
      >
        <Flex flexDirection="col" bg="canvas" w="full" alignItems="center">
          <Box
            // @ts-expect-error
            p="5vw"
            // @ts-expect-error
            maxW="main"
            w="full"
            my={0}
            mx="auto"
          >
            {node && (
              <ParsedDocs
                key={pathname}
                uri={`${basePath}${pathname}`}
                node={node}
                nodeTitle={node.name}
                location={location}
                exportProps={exportProps}
                tryItCredentialsPolicy="include"
              />
            )}
            {data && <Article {...{ isFetched, isLoading, error, data }} />}
          </Box>
        </Flex>
      </Box>
    </>
  )
}
